import React from 'react';
import { Star, Shield, Calendar, Check, X } from 'lucide-react';

interface TrustScore {
  overall: number;
  factors: {
    name: string;
    score: number;
    description: string;
  }[];
}

interface RoommateProfile {
  id: string;
  display_name: string;
  photo_url?: string;
  bio: string;
  verified: boolean;
  trust_score: TrustScore;
  move_in_date: string;
  compatibility: {
    score: number;
    factors: {
      name: string;
      score: number;
      description: string;
    }[];
  };
}

interface RoommateMatchProps {
  profile: RoommateProfile;
}

export function RoommateMatch({ profile }: RoommateMatchProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 mx-auto md:mx-0">
            {profile.photo_url ? (
              <img 
                src={profile.photo_url} 
                alt={profile.display_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-primary-100 flex items-center justify-center text-primary-600 text-2xl font-semibold">
                {profile.display_name.charAt(0)}
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
              <h3 className="text-xl font-semibold">{profile.display_name}</h3>
              {profile.verified && (
                <Shield className="w-5 h-5 text-primary-400" />
              )}
            </div>
            
            <div className="flex items-center justify-center md:justify-start gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Moving {new Date(profile.move_in_date).toLocaleDateString()}</span>
              </div>
            </div>
            
            <p className="text-gray-700 mb-4 text-center md:text-left">{profile.bio}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-primary-400" />
                  <h4 className="font-semibold">Compatibility</h4>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full">
                    <div 
                      className="h-2 bg-primary-400 rounded-full"
                      style={{ width: `${profile.compatibility.score}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{profile.compatibility.score}%</span>
                </div>
                <ul className="space-y-2 text-sm">
                  {profile.compatibility.factors.map((factor, index) => (
                    <li key={index} className="flex items-center gap-2">
                      {factor.score >= 70 ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <X className="w-4 h-4 text-red-500" />
                      )}
                      <span>{factor.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-cream-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-earth-400" />
                  <h4 className="font-semibold">Trust Score</h4>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full">
                    <div 
                      className="h-2 bg-earth-400 rounded-full"
                      style={{ width: `${profile.trust_score.overall}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{profile.trust_score.overall}%</span>
                </div>
                <ul className="space-y-2 text-sm">
                  {profile.trust_score.factors.map((factor, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-earth-100 text-earth-600 flex items-center justify-center text-xs">
                        {Math.round(factor.score / 10)}
                      </span>
                      <span>{factor.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}